import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import Topbar from '../../components/shared/Topbar';
import LandlordSidebar from '../../components/landlord/LandlordSidebar';
import LegalFooter from '../../components/shared/LegalFooter';
import '../../styles/global.css';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmt = (n) => parseFloat(n || 0).toFixed(2);

export default function LandlordTenants() {
  const [dashboard, setDashboard] = useState(null);
  const [allUnits, setAllUnits] = useState([]);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/landlord/dashboard')
      .then(async res => {
        setDashboard(res.data);
        // Load units for all properties
        const allUnitData = [];
        for (const prop of res.data.properties || []) {
          const unitRes = await api.get(`/landlord/properties/${prop.id}/units`);
          unitRes.data.units.forEach(u => allUnitData.push({ ...u, property_name: prop.name, property_id: prop.id }));
        }
        setAllUnits(allUnitData);
      })
      .catch(() => toast.error('Could not load tenants'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = allUnits.filter(u => {
    const nameMatch = search === '' ||
      `${u.first_name} ${u.last_name} ${u.email} ${u.unit_number}`.toLowerCase().includes(search.toLowerCase());
    const statusMatch = filterStatus === 'all' || u.status === filterStatus;
    return nameMatch && statusMatch;
  });

  const activeTenants = allUnits.filter(u => u.status === 'active');

  const exportCSV = () => {
    const headers = ['First Name', 'Last Name', 'Email', 'Phone', 'Unit', 'Property', 'Status', 'Plan', 'Enrolled'];
    const rows = filtered.filter(u => u.first_name).map(u => [
      u.first_name, u.last_name, u.email || '', u.phone || '',
      u.unit_number, u.property_name, u.status || '',
      u.plan_name || '', u.created_at ? new Date(u.created_at).toLocaleDateString() : ''
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => '"' + String(v).replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = 'my_tenants_' + new Date().toISOString().split('T')[0] + '.csv';
    a.click(); URL.revokeObjectURL(url);
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="app-layout">
      <Topbar role="landlord" />
      <div className="dashboard-layout">
        <LandlordSidebar />
        <main id="main-content" className="main-content">
          <div className="page-title">Tenants</div>
          <div className="page-sub">All enrolled tenants across your portfolio.</div>

          <div className="metric-grid">
            <div className="metric-card">
              <div className="metric-label">Total enrolled</div>
              <div className="metric-value">{activeTenants.length}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Pending activation</div>
              <div className="metric-value" style={{ color: 'var(--orange)' }}>
                {allUnits.filter(u => u.status === 'pending').length}
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Vacant units</div>
              <div className="metric-value" style={{ color: 'var(--text-sub)' }}>
                {allUnits.filter(u => u.status === 'vacant').length}
              </div>
            </div>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <input
              className="form-input"
              style={{ maxWidth: 280 }}
              placeholder="Search by name, email, or unit..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <select className="form-input" style={{ maxWidth: 160 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="vacant">Vacant</option>
            </select>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">All tenants ({filtered.length})</div>
              <button className="btn btn-outline btn-sm" onClick={exportCSV}>Export CSV</button>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Tenant</th>
                    <th>Property</th>
                    <th>Unit</th>
                    <th>Plan</th>
                    <th>Monthly</th>
                    <th>Status</th>
                    <th>Enrolled</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-sub)' }}>No tenants found</td></tr>
                  )}
                  {filtered.map(u => (
                    <tr key={u.id}>
                      <td>
                        {u.first_name ? (
                          <div>
                            <div style={{ fontWeight: 600 }}>{u.first_name} {u.last_name}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-sub)' }}>{u.email}</div>
                          </div>
                        ) : <span style={{ color: 'var(--text-sub)' }}>—</span>}
                      </td>
                      <td style={{ fontSize: 12 }}>{u.property_name}</td>
                      <td style={{ fontWeight: 700 }}>{u.unit_number}</td>
                      <td>{u.plan_name || '—'}</td>
                      <td style={{ fontWeight: 700 }}>{u.bulknet_price ? `$${fmt(u.bulknet_price)}` : '—'}</td>
                      <td>
                        <span className={`badge badge-${u.status === 'active' ? 'green' : u.status === 'pending' ? 'orange' : 'gray'}`}>
                          {u.status}
                        </span>
                      </td>
                      <td style={{ fontSize: 12 }}>{fmtDate(u.enrolled_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <LegalFooter />
        </main>
      </div>
    </div>
  );
}
